import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { Building2, LocateFixed, Paperclip, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import * as yup from "yup";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { FormLabel } from "../../components/Core/AppText/AppText";
import { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import CustomerAutoComplete from "../../components/Core/AutoComplete/CustomerAutoComplete";
import Button from "../../components/Core/Button/Button";
import CheckboxNew from "../../components/Core/Checkbox/CheckboxNew";
import { FormCard } from "../../components/Core/FormCard";
import TextAreaNew from "../../components/Core/TextArea/TextAreaNew";
import OutlineButton from "../../components/Core/OutlineButton";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import CompetencePillsController from "../../components/Shared/CompetencePills/CompetencePillsController";
import { ContactPersonController } from "../../components/Shared/ContactPerson/ContactPerson";
import FileImporter from "../../components/Shared/FileImporter/FileImporter";
import FileThumbnail from "../../components/Shared/FileImporter/FileThumbnail";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import ShiftPlanner, {
	ShiftPlannerHandle,
	ShiftPlannerShift,
	plannerSummary,
} from "../../components/Shared/ShiftPlanner";
import TagPicker from "../../components/Shared/TagPicker/TagPicker";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldDefaults,
	customFieldFormValues,
	customFieldsSchema,
	inheritedCustomFieldsFromCustomer,
	jobFormCustomFieldDefinitions,
} from "../../components/Shared/CustomFields";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { JobCreateBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import useQuery from "../../hooks/useQuery";
import Customer from "../../models/Customer";
import Job, { BookingTypes, CreateJobRequest, FileData, ShiftViewModel } from "../../models/Job";
import { KeyValue } from "../../models/KeyValue";
import {
	useCompetencesByCustomer,
	useCustomer,
	useCustomerUsersByCustomer,
} from "../../networking/customer.hooks";
import { useContract } from "../../networking/contract.hooks";
import { useCreateJob, useJob } from "../../networking/job.hooks";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import JobRequirementsEditor, {
	useEditableJobRequirements,
} from "./components/JobRequirementsEditor";
import contractPersonsSchema from "../../schemas/contractPersonsSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import shiftsSchema from "../../schemas/shiftsSchema";
import { plannerShiftInterval } from "./jobMatching.utils";
import { formatNumber } from "../../utils/date.time.utils";
import { removeUndefined } from "../../utils/object.utils";
import { invoiceReferenceHint } from "../../utils/job.utils";
import { formatRoute, jobRoute } from "../../utils/route.utils";
import { randomUuid } from "../../utils/string.utils";
import { toast } from "../../utils/toast";
import { useMediaStore } from "../../stores/media.store";

const JobInputsValidationSchema = yup.object().shape({
	address: yup.object().shape({
		postalCode: postalCodeSchema,
	}),
	// customer: yup.object().required("FIELD_IS_REQUIRED"),
	competence: yup.object().shape({
		branch: yup.string().required("BRANCH_IS_REQUIRED"),
		service: yup.string().required("SERVICE_IS_REQUIRED"),
	}),
	contactPersons: contractPersonsSchema,
	bookingType: yup.string().required("FIELD_IS_REQUIRED"),
	shifts: shiftsSchema,
});

/** Applied to a FormCard whose fields failed validation. */
const INVALID_SECTION_CLASS = "border-red-500 bg-red-50/40";

/**
 * Flattens a react-hook-form error tree into its leaf messages (i18n keys), so
 * the submit bar can name what's actually wrong instead of a generic
 * "there are errors" line. Nested objects and field arrays are both plain
 * objects here, so a single recursive walk covers them.
 */
function collectErrorMessages(errors: unknown, acc: string[] = []): string[] {
	if (!errors || typeof errors !== "object") return acc;

	const { message } = errors as { message?: unknown };
	if (typeof message === "string" && message) {
		if (!acc.includes(message)) acc.push(message);
		return acc;
	}

	// `ref` holds the DOM node RHF attached to the field — never walk into it.
	Object.entries(errors).forEach(([key, value]) => {
		if (key !== "ref") collectErrorMessages(value, acc);
	});
	return acc;
}

const JobCreatePage: React.FC = () => {
	const mediaStore = useMediaStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const query = useQuery();

	const jobIdToCopy = query.get("copy");

	// When copying (?copy=<jobId>), fetch the source job and project the fields
	// that carry over to a new job (this used to be JobStore.setJobCopy, handed
	// over via a MobX observable before navigation). Shifts are an expand on
	// GET /job/:id — without it the copy would silently start with no shifts.
	// useJob is a no-op while jobId is empty (enabled guards on it).
	const { job: jobToCopy } = useJob({ jobId: jobIdToCopy ?? "", expand: ["shifts"] });
	const jobCopy: Partial<Job> | undefined = useMemo(() => {
		if (!jobToCopy) return undefined;

		return {
			orderNumber: jobToCopy.orderNumber,
			customer: jobToCopy.customer,
			competence: jobToCopy.competence,
			parent: { id: jobToCopy.id },
			contactPersons: jobToCopy.contactPersons,
			description: jobToCopy.description,
			customerDescription: jobToCopy.customerDescription,
			internalNotes: jobToCopy.internalNotes,
			purchaseOrderNo: jobToCopy.purchaseOrderNo,
			invoiceReference: jobToCopy.invoiceReference,
			customerOrderNumber: jobToCopy.customerOrderNumber,
			shifts: (jobToCopy.shifts || []).map((shift) => ({
				start: shift.start,
				end: shift.end,
				break: shift.break,
				lane: shift.lane,
			})),
			bookingType: jobToCopy.bookingType,
			autoBooking: jobToCopy.autoBooking,
			address: jobToCopy.address,
			tags: jobToCopy.tags,
			requirements: jobToCopy.requirements,
			images: jobToCopy.images,
			documents: jobToCopy.documents,
			customFields: jobToCopy.customFields,
		};
	}, [jobToCopy]);

	const { documentList, document, documentLoading } = mediaStore;

	const shouldCopyJob = !!jobIdToCopy && jobCopy?.parent?.id === jobIdToCopy;

	// The planner draft is the single source of truth for the shifts UI; every
	// change is mirrored into the form's `shifts` field (as ShiftViewModel) so
	// shiftsSchema keeps validating and submit reads it like before.
	const [plannerShifts, setPlannerShifts] = useState<ShiftPlannerShift[]>([]);
	// Remounts the planner when a copied job's shifts land, so it opens focused
	// on the copied schedule instead of the empty week it mounted with.
	const [plannerKey, setPlannerKey] = useState("new");
	const plannerRef = useRef<ShiftPlannerHandle>(null);
	// ?customer=<id> preselects the customer (e.g. from the customer context
	// menu's "Skapa jobb"). Seeding the state makes it behave exactly like a
	// manual selection: the autocomplete resolves the name and the transfer
	// effect populates address/description.
	const [customerId, setCustomerId] = useState<string | undefined>(
		query.get("customer") ?? undefined
	);
	// Section cards, in page order, so an invalid submit can scroll the first
	// offending section into view (react-hook-form's own shouldFocusError can't
	// reach the controlled/custom widgets — they don't forward a ref).
	const matchingCardRef = useRef<HTMLElement>(null);
	const contactCardRef = useRef<HTMLElement>(null);
	const shiftsCardRef = useRef<HTMLElement>(null);
	const bookingCardRef = useRef<HTMLElement>(null);
	const customFieldsCardRef = useRef<HTMLElement>(null);

	const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
	const [imageFiles, setImageFiles] = useState<FileData[]>([]);
	const [documentFiles, setDocumentFiles] = useState<FileData[]>([]);

	const { data: organisation } = useOrganisation();

	// Org-defined custom fields: the job's own plus the customer fields the job
	// inherits (shown read-only or overridable). Required-ness is form-only, so
	// their validation is appended to the static schema (below, once the
	// selected customer's values are known).
	const jobCustomFieldDefinitions = useCustomFieldDefinitions("job");
	const customerCustomFieldDefinitions = useCustomFieldDefinitions("customer");
	const customFieldDefinitions = useMemo(
		() => jobFormCustomFieldDefinitions(jobCustomFieldDefinitions, customerCustomFieldDefinitions),
		[jobCustomFieldDefinitions, customerCustomFieldDefinitions]
	);

	// DESCRIPTIVE labels (job.tags, org jobTagTypes) — never affect matching.
	const [jobTagKeys, setJobTagKeys] = useState<string[]>([]);

	const fileUploadRules = [
		{
			validTypes: ["image/png", "image/jpeg"],
			maxQuantity: 2,
			warningMessage: `${t("MAXIMUM_IMAGE_COUNT")} 2`,
			currentQuantity: imageFiles.length ?? 0,
		},
		{
			validTypes: ["application/pdf"],
			maxQuantity: 2,
			warningMessage: `${t("MAXIMUM_DOCUMENT_COUNT")} 2`,
			currentQuantity: documentFiles.length ?? 0,
		},
	];

	/** Planner draft → the form's ShiftViewModel list (what shiftsSchema
	 * validates and onSubmit reads — `shift` holds the ISO start/end). */
	function toShiftViewModels(shifts: ShiftPlannerShift[]): ShiftViewModel[] {
		return shifts.map((shift) => {
			const { start, end } = plannerShiftInterval(shift);
			return {
				localId: shift.id,
				shift: { start, end, break: shift.breakHours, lane: shift.lane },
			};
		});
	}

	function handlePlannerChange(shifts: ShiftPlannerShift[]) {
		setPlannerShifts(shifts);
		setValue("shifts", toShiftViewModels(shifts), { shouldValidate: true });
	}

	// Marks the job id whose copy has been applied to the form. The source job
	// loads asynchronously, so the form starts empty and is reset once the data
	// arrives — exactly once, so a background refetch can't clobber edits.
	const copiedFromJob = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!shouldCopyJob || !jobCopy || copiedFromJob.current === jobCopy.parent?.id) return;
		copiedFromJob.current = jobCopy.parent?.id;

		const copiedShifts: ShiftPlannerShift[] = (jobCopy.shifts || []).map((shift) => ({
			id: randomUuid(),
			date: dayjs(shift.start).format("YYYY-MM-DD"),
			startTime: dayjs(shift.start).format("HH:mm"),
			endTime: dayjs(shift.end).format("HH:mm"),
			breakHours: shift.break,
			lane: shift.lane,
		}));
		setPlannerShifts(copiedShifts);
		setPlannerKey(jobCopy.parent?.id ?? "copy");

		reset({
			bookingType: jobCopy.bookingType,
			competence: jobCopy.competence,
			contactPersons: jobCopy.contactPersons,
			shifts: toShiftViewModels(copiedShifts),
			address: jobCopy.address,
			internalNotes: jobCopy.internalNotes,
			customerDescription: jobCopy.customerDescription,
			description: jobCopy.description,
			purchaseOrderNo: jobCopy.purchaseOrderNo,
			invoiceReference: jobCopy.invoiceReference,
			customerOrderNumber: jobCopy.customerOrderNumber,
			customFields: jobCopy.customFields
				? customFieldFormValues(customFieldDefinitions, jobCopy.customFields)
				: customFieldDefaults(customFieldDefinitions),
		});

		// Trigger change so competences etc are loaded
		handleCustomerChange(jobCopy.customer as { id: string; name: string }, false);

		// Carry over the copied job's descriptive tags and its requirement gate.
		// The copied requirements are unioned with the rule-derived set for the
		// (copied) competence once the form reset lands, so manual additions on
		// the source job carry over while the org's matchningsregler still apply.
		if (jobCopy.tags?.length) {
			setJobTagKeys(jobCopy.tags.map((tag) => tag.key));
		}
		resetRequirements(jobCopy.requirements);
		if (jobCopy.images) {
			setImageFiles(jobCopy.images);
		}
		if (jobCopy.documents) {
			setDocumentFiles(jobCopy.documents);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldCopyJob, jobCopy]);

	// Marks which customer's fields have already been copied into the form, so
	// the transfer runs once per selection (see the effect below).
	const transferredForCustomer = useRef<string | undefined>(undefined);

	// isInitialLoading, not isLoading: with no customer selected the query is
	// disabled, and a disabled query still reports isLoading=true (no data yet).
	const { customer: selectedCustomer, isInitialLoading: isCustomerFetching } =
		useCustomer(customerId);
	const { customerUsers } = useCustomerUsersByCustomer(
		{ customerId: customerId ?? "" },
		!!customerId
	);

	// The selected customer's contract, used to enable/price competence services.
	const { contract: selectedCustomerContract, isInitialLoading: isContractLoading } = useContract(
		{ contractId: selectedCustomer?.contract?.id, inheritFromFallback: true },
		!!selectedCustomer?.contract?.id
	);

	const isCustomerLoading = isCustomerFetching || isContractLoading;

	// The selected customer's values for its inheritable fields — shown as
	// "Ärvd från kund" on the job form and what a required inheritable field
	// may fall back on.
	const inheritedCustomFields = useMemo(
		() => inheritedCustomFieldsFromCustomer(selectedCustomer, customerCustomFieldDefinitions),
		[selectedCustomer, customerCustomFieldDefinitions]
	);
	const validationSchema = useMemo(
		() =>
			JobInputsValidationSchema.shape({
				customFields: customFieldsSchema(customFieldDefinitions, inheritedCustomFields),
			}),
		[customFieldDefinitions, inheritedCustomFields]
	);

	const { mutateAsync: createJob } = useCreateJob();

	const BOOKING_TYPE_OPTIONS = Object.keys(BookingTypes).map((bookingType) => ({
		key: bookingType,
		value: t(`BOOKING_TYPE_${bookingType}` as any),
	}));

	// When copying, the source job loads async — the copy effect above resets
	// the form with the copied values once they arrive.
	const { register, handleSubmit, setValue, watch, control, formState, reset } = useForm<
		Omit<CreateJobRequest, "shifts" | "customFields"> & {
			shifts: ShiftViewModel[];
			customFields?: CustomFieldFormValues;
		}
	>({
		mode: "onBlur",
		resolver: yupResolver(validationSchema),
		defaultValues: {
			bookingType: BookingTypes.OPEN,
			competence: { branch: "", service: "" },
			contactPersons: [],
			shifts: [],
			customFields: {},
		},
		// Handled by scrollToFirstInvalidSection below — RHF's focus would fight
		// the section scroll, and it can't reach the controlled widgets anyway.
		shouldFocusError: false,
	});

	// Seed the custom fields with their definition defaults once the org has
	// loaded — once only, so a background org refetch can't clobber edits (and
	// the copy flow above, which resets them from the source job, wins).
	const customFieldsSeeded = useRef(false);
	useEffect(() => {
		if (customFieldsSeeded.current || customFieldDefinitions.length === 0) return;
		customFieldsSeeded.current = true;
		if (!shouldCopyJob) setValue("customFields", customFieldDefaults(customFieldDefinitions));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customFieldDefinitions]);

	const { competences } = useCompetencesByCustomer({
		customerId,
		inherited: true,
	});

	const watchedCompetence = watch("competence");

	// The selected customer's tags — customer-tag scoped matchningsregler
	// resolve against these. `undefined` while the customer is being fetched
	// (defers the rule derivation), `[]` when no customer is selected yet.
	const customerTags = customerId
		? selectedCustomer
			? selectedCustomer.tags ?? []
			: undefined
		: [];

	// The MATCHING GATE (job.requirements): suggested from the org's
	// matchningsregler for the selected competence and customer, freely
	// editable per job (add/remove certifications and consultant tags),
	// snapshotted at create.
	const { requirements, setRequirements, resetRequirements } = useEditableJobRequirements(
		organisation,
		watchedCompetence,
		customerTags
	);

	// Everything below the customer card depends on the selected customer —
	// until one is picked those sections render as skeletons.
	const hasCustomer = !!customerId;
	const hasCompetence = !!watchedCompetence?.branch && !!watchedCompetence?.service;

	const showRequirementsEditor =
		hasCompetence &&
		((organisation?.certificationTypes?.length ?? 0) > 0 ||
			(organisation?.consultantTagTypes?.length ?? 0) > 0 ||
			requirements.certifications.length > 0 ||
			requirements.tags.length > 0);

	const jobTagTypes = organisation?.jobTagTypes ?? [];

	/** Form field → the card that holds it, in page order. */
	const errorSections: { field: string; ref: React.RefObject<HTMLElement> }[] = [
		{ field: "competence", ref: matchingCardRef },
		{ field: "address", ref: contactCardRef },
		{ field: "contactPersons", ref: contactCardRef },
		{ field: "shifts", ref: shiftsCardRef },
		{ field: "bookingType", ref: bookingCardRef },
		{ field: "customFields", ref: customFieldsCardRef },
	];

	const scrollToFirstInvalidSection = (errors: object) => {
		const section = errorSections.find(({ field }) => (errors as Record<string, unknown>)[field]);
		section?.ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!customerId) return;

		if (data.competence?.skills?.length === 0) delete data.competence.skills;

		const bookingTypeInvitationSelected = data.bookingType === BookingTypes.INVITATION;

		const createOrderRequest: CreateJobRequest = {
			customer: { id: customerId },
			competence: data.competence,
			address: data.address,
			contactPersons: [],
			description: data.description,
			customerDescription: data.customerDescription,
			internalNotes: data.internalNotes,
			shifts: data.shifts.map((s) => s.shift),
			numPositions: 1,
			// Note: For now treat INVITATION as OPEN but admin user can choose to invite consultants to job
			bookingType:
				data.bookingType === BookingTypes.INVITATION
					? BookingTypes.OPEN
					: (data.bookingType as BookingTypes),
			hidden: data.hidden,
			purchaseOrderNo: data.purchaseOrderNo,
			invoiceReference: data.invoiceReference,
			customerOrderNumber: data.customerOrderNumber,
			gflRate: data.gflRate ? Number(data.gflRate) : 0,
			parent: shouldCopyJob ? jobCopy?.parent : undefined,
			// invitations: data.bookingType === BookingTypes.INVITATION ? invitations : undefined,
			images: imageFiles.length > 0 ? imageFiles : undefined,
			documents: documentFiles.length > 0 ? documentFiles : undefined,
			// The matching gate — always sent explicitly (even when empty) so the
			// admin's edits win over the backend's rule derivation.
			requirements,
			// Descriptive labels (jobTagTypes) — structurally separate from the
			// requirement gate.
			tags: jobTagKeys.length > 0 ? jobTagKeys.map((key) => ({ key })) : undefined,
			// Always the full map (the backend replaces it wholesale); undefined
			// when the org has no job fields so the payload stays untouched.
			customFields: buildCustomFieldsPayload(data.customFields, customFieldDefinitions),
		};

		if (data.contactPersons.length) {
			if (data.contactPersons[0].profile) removeUndefined(data.contactPersons[0].profile);
			removeUndefined(data.contactPersons[0]);

			createOrderRequest.contactPersons = data.contactPersons;
		}

		try {
			const createdJob = await createJob(createOrderRequest);

			toast.success(t("JOB_CREATED_SUCCESSFULLY"), {
				action: {
					label: t("JOB_VIEW_CREATED"),
					onClick: () => navigate(jobRoute(createdJob.id)),
				},
			});

			if (bookingTypeInvitationSelected) {
				navigate(jobRoute(createdJob.id));
			} else {
				navigate(routes.JOBS);
			}
		} catch (e: any) {
			toast.error(e?.error?.detail || e?.detail);
		}
	}, scrollToFirstInvalidSection);

	const handleCustomerChange = (
		e: Pick<Customer, "id" | "name"> | undefined,
		transferCustomerData = true
	) => {
		// Mark the customer as already-transferred when its fields should NOT be
		// populated into the form (i.e. when copying a job).
		transferredForCustomer.current = transferCustomerData ? undefined : e?.id;
		setCustomerId(e?.id);
	};

	// Populate customer-derived form fields when the selected customer has
	// loaded (the contract loads declaratively via useContract above). The ref
	// makes the field transfer run once per selection so a background refetch
	// can't clobber the user's edits.
	useEffect(() => {
		if (!selectedCustomer || transferredForCustomer.current === selectedCustomer.id) return;

		transferredForCustomer.current = selectedCustomer.id;
		setValue("address.address1", selectedCustomer.visitingAddress?.address1);
		setValue("address.city", selectedCustomer.visitingAddress?.city);
		setValue("address.postalCode", selectedCustomer.visitingAddress?.postalCode);
		setValue("customerDescription", selectedCustomer.description);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCustomer?.id]);

	const fileRemoveHandler = (fileUrl: string) => {
		if (imageFiles.find((f) => f.url === fileUrl)) {
			setImageFiles((prev) => prev.filter((fi) => fi.url !== fileUrl));
		} else {
			setDocumentFiles((prev) => prev.filter((df) => df.url !== fileUrl));
		}
	};

	const addAddFileHandler = () => {
		const listData = documentList ?? [];
		if (document) {
			listData.push(document);
		}
		listData?.forEach((d) => {
			if (d.mimeType === "application/pdf") {
				setDocumentFiles((prev) => [...prev, { url: d.url, name: d.originalName }]);
			}
			if (["image/png", "image/jpeg"].includes(d.mimeType)) {
				setImageFiles((prev) => [...prev, { url: d.url, name: d.originalName }]);
			}
		});
		mediaStore.removeDocumentList();
		mediaStore.removeDocument();
	};

	const attachedFiles = [...imageFiles, ...documentFiles];
	const shiftsSummary = plannerSummary(plannerShifts);
	const errorMessages = collectErrorMessages(formState.errors);

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={JobCreateBreadcrumbs} />

			<form onSubmit={onSubmit}>
				{/* Wide enough for the ShiftPlanner's week grid (label column + 7 days)
				    to breathe — the form cards above it are the same width. */}
				<div className="mx-auto flex max-w-[1120px] flex-col gap-5 py-6 pb-28">
					{shouldCopyJob && (
						<div className="rounded-lg border border-solid border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
							{t("job.copyInfo", { orderNumber: jobCopy?.orderNumber })}
						</div>
					)}

					{/* 1. Customer — the entry point, everything else depends on it */}
					<FormCard
						title="CUSTOMER"
						action={
							hasCustomer ? (
								<Link
									to={formatRoute(routes.CUSTOMER_DETAILS, [customerId])}
									target="_blank"
									className="text-sm font-medium text-action underline"
								>
									Visa kund (öppnas i ny flik)
								</Link>
							) : undefined
						}
					>
						<CustomerAutoComplete
							width={"100%"}
							onChange={(customerId, customer) => handleCustomerChange(customer)}
							value={customerId}
							prefix=""
							placeholder="SELECT_CUSTOMER"
							active
							autoFocus
						/>
						{!hasCustomer && (
							<p className="mb-0 mt-3 text-sm text-muted-foreground">
								Börja med att välja kund — övriga fält låses upp därefter.
							</p>
						)}
						{/* The picker collapses to "<name> (#<nr>)" once a customer is
						    chosen, so the group is repeated here — it is what tells
						    look-alike units in different koncerner apart. */}
						{hasCustomer && selectedCustomer?.corporateGroup?.name && (
							<p className="mb-0 mt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
								<Building2 className="h-4 w-4 shrink-0" aria-hidden />
								{t("corporateGroup.title")}:{" "}
								<span className="font-medium text-foreground">
									{selectedCustomer.corporateGroup.name}
								</span>
							</p>
						)}
					</FormCard>

					{/* Everything below depends on the selected customer. The real fields
					    render from the start but are locked + dimmed until a customer is
					    chosen — a skeleton here read as "loading" rather than "action
					    required". The fieldset disables all native controls; the
					    pointer-events guard covers the custom (non-native) widgets. */}
					<fieldset
						disabled={!hasCustomer}
						className={cn(
							"m-0 flex min-w-0 flex-col gap-5 border-0 p-0 transition-opacity",
							!hasCustomer && "pointer-events-none opacity-60"
						)}
					>
						{/* 2. Competence + the matching gate it drives */}
						<FormCard
							title="MATCHING"
							ref={matchingCardRef}
							className={cn(formState.errors?.competence && INVALID_SECTION_CLASS)}
						>
							{isCustomerLoading ? (
								// Honest loading state — the selected customer's competences and
								// contract are actually being fetched here.
								<div className="flex flex-col gap-4">
									<div className="flex flex-wrap gap-2">
										<Skeleton className="h-9 w-28 rounded-full" />
										<Skeleton className="h-9 w-36 rounded-full" />
										<Skeleton className="h-9 w-24 rounded-full" />
									</div>
									<Skeleton className="h-4 w-64" />
								</div>
							) : (
								<div className="flex flex-col gap-5">
									{competences ? (
										<CompetencePillsController
											competences={competences}
											name="competence"
											control={control}
											errors={formState.errors?.competence}
											enabledServices={selectedCustomerContract?.prices}
										/>
									) : (
										<p className="mb-0 text-sm text-muted-foreground">
											Kompetenser hämtas från vald kund.
										</p>
									)}

									<div className="space-y-2">
										<p className="mb-0 text-sm font-medium text-muted-foreground">
											Certifikat & taggar
										</p>
										{showRequirementsEditor ? (
											<JobRequirementsEditor
												organisation={organisation}
												competence={watchedCompetence}
												customerTags={customerTags}
												value={requirements}
												onChange={setRequirements}
											/>
										) : (
											<p className="mb-0 text-sm text-muted-foreground">
												Välj först kompetens för att kunna ange ytterligare kompetenskrav.
											</p>
										)}
									</div>
								</div>
							)}
						</FormCard>

						<FormCard
							title="Plats & kontaktperson"
							skipTranslation
							ref={contactCardRef}
							className={cn(
								(formState.errors?.contactPersons || formState.errors?.address) &&
									INVALID_SECTION_CLASS
							)}
						>
							{/* Address and contact person side by side on wide screens,
							    stacked below the lg breakpoint. */}
							<div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
								{/* Mirrors the ContactPerson block's rhythm: an h-9 header row
								    followed by gap-3 rows, so the two columns' inputs line up. */}
								<div className="flex flex-col gap-3">
									<div className="flex h-9 items-center">
										<FormLabel margin="0">{t("ADDRESS")}</FormLabel>
									</div>
									<AppInputNew
										{...register("address.address1")}
										placeholder="ADDRESS_PLACEHOLDER"
									/>
									<div className="grid grid-cols-2 gap-x-4">
										<AppInputNew
											{...register("address.postalCode")}
											placeholder="POSTAL_CODE_PLACEHOLDER"
											errors={formState.errors?.address?.postalCode?.message}
										/>
										<AppInputNew {...register("address.city")} placeholder="CITY_PLACEHOLDER" />
									</div>
								</div>

								<ContactPersonController
									name="contactPersons"
									control={control}
									errors={formState.errors?.contactPersons}
									customerUsers={customerUsers ?? []}
								/>
							</div>
						</FormCard>

						<FormCard
							title="JOB_DESCRIPTION"
							action={
								<OutlineButton
									label={`${t("UPLOAD_DOCUMENTS")}${
										attachedFiles.length > 0 ? ` (${attachedFiles.length})` : ""
									}`}
									icon={<Paperclip size={14} />}
									onClick={() => setUploadModalVisible(true)}
									disabled={!hasCustomer}
								/>
							}
						>
							<div className="flex flex-col gap-4">
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
									<TextAreaNew
										{...register("description")}
										label="JOB_DESCRIPTION"
										errors={formState.errors?.description?.message}
									/>
									<TextAreaNew
										{...register("customerDescription")}
										label="ABOUT_WORKPLACE"
										errors={formState.errors?.customerDescription?.message}
									/>
								</div>

								<TextAreaNew {...register("internalNotes")} label="PRIVATE_NOTES" height="80px" />

								{attachedFiles.length > 0 && (
									<div className="flex flex-wrap gap-2">
										{attachedFiles.map((f) => (
											<FileThumbnail
												key={f.url}
												onRemove={fileRemoveHandler}
												fileName={f.name}
												fileUrl={f.url}
											/>
										))}
									</div>
								)}

								{jobTagTypes.length > 0 && (
									<div>
										<p className="mb-0 text-sm font-medium text-muted-foreground">Taggar</p>
										<p className="mb-1.5 mt-0.5 text-sm text-muted-foreground">
											Beskrivande etiketter för uppdragslistan — påverkar inte matchningen.
										</p>
										<TagPicker
											options={jobTagTypes}
											value={jobTagKeys}
											onChange={setJobTagKeys}
											placeholder="Lägg till tagg…"
										/>
									</div>
								)}
							</div>
						</FormCard>

						<FormCard
							title="SHIFTS"
							ref={shiftsCardRef}
							className={cn(formState.errors?.shifts && INVALID_SECTION_CLASS)}
							action={
								<div className="flex items-center gap-3">
									{plannerShifts.length > 0 && (
										<span className="text-sm text-muted-foreground">
											{shiftsSummary.days} {t("shiftPlanner.daysUnit")} ·{" "}
											{formatNumber(shiftsSummary.hours, 0)} {t("shiftPlanner.hoursUnit")}
										</span>
									)}
									<OutlineButton
										label={t("shiftPlanner.today")}
										icon={<LocateFixed size={14} />}
										onClick={() => plannerRef.current?.scrollToDate(dayjs().format("YYYY-MM-DD"))}
									/>
									{plannerShifts.length > 0 && (
										// Create-only chrome: the whole draft is disposable here.
										// The edit view (JobMatchingTab) deliberately has no
										// clear-all — there shifts carry bookings/time reports.
										<OutlineButton
											label={t("shiftPlanner.clearAll")}
											icon={<Trash2 size={14} />}
											onClick={() => handlePlannerChange([])}
										/>
									)}
								</div>
							}
						>
							<>
								<p className="mb-3 mt-0 text-sm text-muted-foreground">{t("job.shifts.hint")}</p>
								<ShiftPlanner
									key={plannerKey}
									ref={plannerRef}
									shifts={plannerShifts}
									consultants={[]}
									onChange={handlePlannerChange}
									narrow
									className="max-h-[55vh]"
								/>
								{formState.errors?.shifts && (
									<p className="mb-0 mt-2 text-sm text-danger">
										{t((formState.errors.shifts as { message?: string }).message as any)}
									</p>
								)}
							</>
						</FormCard>

						<FormCard
							title="Bokning & referenser"
							skipTranslation
							ref={bookingCardRef}
							className={cn(formState.errors?.bookingType && INVALID_SECTION_CLASS)}
						>
							{/* One two-column grid for everything on the card: the booking block,
							    the built-in inputs and the custom fields (incl. inherited customer
							    fields) flow as cells, so the card grows row by row however many
							    fields the org adds. */}
							<div className="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2">
								<div className="flex flex-col gap-5">
									<AppSelectController<KeyValue>
										control={control}
										label="BOOKING_TYPE"
										name="bookingType"
										options={BOOKING_TYPE_OPTIONS}
										getOptionLabel={(o) => o.value}
										getOptionValue={(o) => o.key as string}
										errors={formState.errors?.bookingType?.message}
										placeholder="SELECT_BOOKING_TYPE"
										margin="0"
										mandatory
										knowledgeBaseTag="booking-type"
									/>
									{watch("bookingType") === BookingTypes.INVITATION && (
										<p className="m-0 text-sm text-muted-foreground">
											{t("job.inviteInstructions")}
										</p>
									)}
									<CheckboxNew
										{...register("hidden")}
										label="job.doNotPublish"
										hint="job.doNotPublishDesc"
									/>
								</div>

								<AppInputNew {...register("purchaseOrderNo")} label="job.purchaseOrderNo" />
								<AppInputNew
									{...register("invoiceReference")}
									label="job.invoiceReference"
									description={invoiceReferenceHint(t, selectedCustomer?.invoiceDetails?.reference)}
								/>
								<AppInputNew {...register("customerOrderNumber")} label="job.customerOrderNo" />
								<AppInputNew
									{...register("gflRate", { valueAsNumber: true })}
									label="job.gflRate"
									type="number"
								/>
								<CustomFieldsSection
									entity="job"
									placement="bokning"
									variant="inline"
									className="contents"
									definitions={customFieldDefinitions}
									inherited={inheritedCustomFields}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</div>
						</FormCard>

						{/* Org-defined custom fields placed under "Övriga uppgifter" — hidden when there are none. */}
						<CustomFieldsSection
							entity="job"
							placement="ovrigt"
							definitions={customFieldDefinitions}
							inherited={inheritedCustomFields}
							control={control}
							register={register}
							errors={formState.errors}
							cardRef={customFieldsCardRef}
							className={cn(formState.errors?.customFields && INVALID_SECTION_CLASS)}
						/>
					</fieldset>
				</div>

				<FloatingButtonPanel>
					{/* Names what is actually missing, and clicking it jumps to the first
					    offending section — the generic "there are errors" line left the
					    user hunting through five cards. */}
					{errorMessages.length > 0 && (
						<button
							type="button"
							onClick={() => scrollToFirstInvalidSection(formState.errors)}
							className="m-3 mb-0 max-w-lg cursor-pointer rounded-md border border-solid border-red-500 bg-white px-3 py-2 text-left text-sm text-red-700 shadow-sm"
						>
							<span className="font-medium">{t("ONE_OR_MORE_ERRORS_IN_FORM")}:</span>{" "}
							{errorMessages.map((message) => t(message as any)).join(" · ")}
						</button>
					)}
					<Button disabled={formState.isSubmitting || !hasCustomer}>{t("CREATE_JOB")}</Button>
				</FloatingButtonPanel>

				<Modal size={"xl"} isOpen={uploadModalVisible} onClose={() => setUploadModalVisible(false)}>
					<ModalOverlay />
					<ModalContent p={4}>
						<ModalHeader textAlign="center">{t("UPLOAD_JOB_DOCUMENTS")}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							<FileImporter
								maxFiles={4}
								multipleFiles
								maxFileSize={20000000}
								acceptedTypes={["image/png", "application/pdf", "image/jpeg"]}
								uploadRules={fileUploadRules}
							/>
						</ModalBody>

						<VStack>
							<Button
								disabled={documentLoading || documentList?.length === 0}
								width="220px"
								margin="2.5rem 0 0 0"
								fontSize="s"
								height="34px"
								loading={documentLoading}
								onClick={() => {
									addAddFileHandler();
									setUploadModalVisible(false);
								}}
							>
								{t("UPLOAD")}
							</Button>
							<Button
								onClick={() => setUploadModalVisible(false)}
								width="220px"
								margin="0.625rem 0 0 0"
								textColor="black"
								backgroundColor="buttonGray"
								fontSize="s"
								height="34px"
							>
								{t("CANCEL")}
							</Button>
						</VStack>
					</ModalContent>
				</Modal>
			</form>
		</div>
	);
};

export default JobCreatePage;
